import {
  BrandMenuProviderResponse,
  BrandMenuResponse,
  CreateBrandMenuDto,
  CreateMenuCategoryV2Dto,
  CreateMenuModifierV2Dto,
  GetPublishSyncStatusResponse,
  ImportMenuV2Dto,
  MenuImportAiPreviewResponse,
  MenuImportAiStartResponse,
  MenuNamesResponse,
  MenuPublishHistoryDetailResponse,
  MenuPublishHistoryItemResponse,
  PublishMenuDto,
  UpdateBrandMenuDto,
  UpdateBrandMenuProvidersDto,
  UpdateMenuCategoryV2Dto,
  UpdateMenuModifierGroupV2Dto,
  UpdateMenuScheduleV2Dto,
} from '../types/api/menu.types';
import {
  ImportItemsFromPosMenuDto,
  ImportMenuUrlPreviewV2Response,
  ItemType,
  MenuV2Response,
  UpdateMenuListOrders,
  UpdateMenuPosModifierOptionDto,
} from '../types/menu.types';
import {
  PaginationParams,
  Provider,
  ResponseType,
} from '../types/shared.types';
import { api } from './api';

/**
 * A brand has several menus; routes that work on a whole menu take the one to
 * use as `?menuId=`. Left out (null / undefined) they work on the brand's
 * default menu, which is also what a client that predates multi-menu gets.
 * Routes on an existing entity (update / delete category, item, modifier
 * group, schedule; item order; sold-out toggles) know the menu from the
 * entity and take no `menuId`.
 */
export type MenuId = number | null | undefined;

const menuIdParams = (menuId: MenuId, extra: Record<string, unknown> = {}) => ({
  params: menuId ? { ...extra, menuId } : extra,
});

// --- Menus of a brand ---

export const getBrandMenus = (
  locationId: string | number,
  brandId: string | number
) =>
  api.get<ResponseType<BrandMenuResponse[]>>(
    `/v2/menu/${locationId}/menus/${brandId}`
  );

/** Answers with the updated menu list. */
export const createBrandMenu = (
  locationId: string | number,
  brandId: string | number,
  data: CreateBrandMenuDto
) =>
  api.post<ResponseType<BrandMenuResponse[]>>(
    `/v2/menu/${locationId}/menus/${brandId}`,
    data
  );

/** Rename, or make this the default. Answers with the updated menu list. */
export const updateBrandMenu = (
  locationId: string | number,
  brandId: string | number,
  menuId: number,
  data: UpdateBrandMenuDto
) =>
  api.put<ResponseType<BrandMenuResponse[]>>(
    `/v2/menu/${locationId}/menus/${brandId}/update/${menuId}`,
    data
  );

/**
 * Deletes the menu with its publish history and AI drafts; its providers move
 * to the default menu, which is published to them right away. The default
 * menu itself cannot be deleted (400). Answers with the updated menu list.
 */
export const deleteBrandMenu = (
  locationId: string | number,
  brandId: string | number,
  menuId: number
) =>
  api.delete<ResponseType<BrandMenuResponse[]>>(
    `/v2/menu/${locationId}/menus/${brandId}/delete/${menuId}`
  );

export const getBrandMenuProviders = (
  locationId: string | number,
  brandId: string | number
) =>
  api.get<ResponseType<BrandMenuProviderResponse[]>>(
    `/v2/menu/${locationId}/menus/${brandId}/providers`
  );

/**
 * Partial update: only the listed providers change. A provider whose menu
 * changes gets that menu's last published version pushed immediately (or the
 * current content if it was never published; an empty menu is not pushed).
 * Answers with the same shape as the GET.
 */
export const updateBrandMenuProviders = (
  locationId: string | number,
  brandId: string | number,
  data: UpdateBrandMenuProvidersDto
) =>
  api.put<ResponseType<BrandMenuProviderResponse[]>>(
    `/v2/menu/${locationId}/menus/${brandId}/providers`,
    data
  );

export const getLocationStoreName = (
  locationId: string | number,
  brandId: string | number
) =>
  api.get<ResponseType<MenuNamesResponse>>(
    `/v1/menu/${locationId}/location/${brandId}/brand/name`
  );

export const getMenu = (
  locationId: string | number,
  brandId: string | number,
  menuId?: MenuId
) =>
  api.get<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/location/${brandId}/brand`,
    menuIdParams(menuId)
  );

export const createMenuCategory = (
  locationId: string | number,
  brandId: string | number,
  data: CreateMenuCategoryV2Dto,
  menuId?: MenuId
) =>
  api.post<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/category/${brandId}`,
    data,
    menuIdParams(menuId)
  );

export const updateMenuCategory = (
  locationId: string | number,
  brandId: string | number,
  id: string | number,
  data: UpdateMenuCategoryV2Dto
) =>
  api.put<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/category/${brandId}/update/${id}`,
    data
  );

export const createMenuItem = (
  locationId: string | number,
  brandId: string | number,
  data: FormData
) =>
  api.post<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/item/${brandId}`,
    data,
    {
      headers: { 'content-type': 'multipart/form-data' },
    }
  );

export const updateMenuItem = (
  locationId: string | number,
  brandId: string | number,
  id: string | number,
  data: FormData
) =>
  api.put<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/item/${brandId}/update/${id}`,
    data,
    {
      headers: { 'content-type': 'multipart/form-data' },
    }
  );

export const updateMenuPosItem = (
  locationId: string | number,
  brandId: string | number,
  id: string | number,
  data: FormData
) =>
  api.put<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/item/${brandId}/update/${id}/pos`,
    data,
    {
      headers: { 'content-type': 'multipart/form-data' },
    }
  );

export const updateMenuPosModifierOption = (
  locationId: string | number,
  brandId: string | number,
  id: string | number,
  data: UpdateMenuPosModifierOptionDto
) =>
  api.put<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/modifier/${brandId}/update/${id}/pos`,
    data
  );

export const createMenuModifierGroup = (
  locationId: string | number,
  brandId: string | number,
  data: CreateMenuModifierV2Dto,
  menuId?: MenuId
) =>
  api.post<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/modifier-group/${brandId}`,
    data,
    menuIdParams(menuId)
  );

export const updateMenuModifierGroup = (
  locationId: string | number,
  brandId: string | number,
  id: string | number,
  data: UpdateMenuModifierGroupV2Dto
) =>
  api.put<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/modifier-group/${brandId}/update/${id}`,
    data
  );

export const deleteMenuTypeItemById = (
  locationId: string | number,
  brandId: string | number,
  id: string | number,
  type: ItemType
) =>
  api.delete<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/delete/${brandId}/item/${id}/${type}`
  );

/**
 * Wipes the whole menu of a location (`brandId` 0) or brand: categories,
 * items, modifier groups and availabilities. Nothing is pushed to the
 * providers - the menu is only flagged as needing a publish, like any other
 * edit. Admin / support only. Returns the (now empty) menu.
 */
export const clearMenu = (
  locationId: string | number,
  brandId: string | number,
  menuId?: MenuId
) =>
  api.delete<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/clear/${brandId}`,
    menuIdParams(menuId)
  );

export const createMenuSchedule = (
  locationId: string | number,
  brandId: string | number,
  data: UpdateMenuScheduleV2Dto,
  menuId?: MenuId
) =>
  api.post<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/schedule/${brandId}`,
    data,
    menuIdParams(menuId)
  );

export const updateMenuSchedule = (
  locationId: string | number,
  brandId: string | number,
  id: string | number,
  data: UpdateMenuScheduleV2Dto
) =>
  api.put<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/schedule/${brandId}/update/${id}`,
    data
  );

export const deleteMenuSchedule = (
  locationId: string | number,
  brandId: string | number,
  id: string | number
) =>
  api.delete<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/schedule/${brandId}/delete/${id}`
  );

// export const migrateMenu = (
//   locationId: string | number,
//   brandId: string | number,
//   provider: Provider
// ) =>
//   api.get<ResponseType<MenuResponse>>(
//     `/v1/menu/${locationId}/migrate/${brandId}/brand/${provider}`
//   );

export const importMenuFromAnotherProviderPreview = (
  locationId: string | number,
  brandId: string | number,
  importProvider: Provider
) =>
  api.post<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/category/${brandId}/preview`,
    {
      importProvider,
    }
  );

export const importMenuFromURL = (
  locationId: string | number,
  brandId: string | number,
  url: string
) =>
  api.post<ResponseType<ImportMenuUrlPreviewV2Response>>(
    `/v2/menu/${locationId}/category/${brandId}/preview-url`,
    {
      url,
    }
  );

export const cancelMenuImportFromUrl = (
  locationId: string | number,
  brandId: string | number,
  url: string
) =>
  api.post<ResponseType<null>>(
    `/v2/menu/${locationId}/category/${brandId}/cancel-preview-url`,
    {
      url,
    }
  );

/** Replaces the content of ONE menu (the selected one), the others stay. */
export const importMenuFromAnotherProvider = (
  locationId: string | number,
  brandId: string | number,
  data: ImportMenuV2Dto,
  menuId?: MenuId
) =>
  api.post<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/category/${brandId}/import`,
    data,
    menuIdParams(menuId)
  );

export const uploadImportPreviewImage = (
  locationId: string | number,
  brandId: string | number,
  data: FormData
) =>
  api.post<ResponseType<string>>(
    `/v2/menu/${locationId}/category/${brandId}/import-image`,
    data,
    {
      headers: { 'content-type': 'multipart/form-data' },
    }
  );

// AI menu import: upload a document (+ optional subPrompt) to start a worker
// job. Returns a jobId; poll `getMenuImportAiPreview` until status is ready.
export const uploadMenuImportAi = (
  locationId: string | number,
  brandId: string | number,
  data: FormData
) =>
  api.post<ResponseType<MenuImportAiStartResponse>>(
    `/v2/menu/${locationId}/category/${brandId}/import-ai/upload`,
    data,
    {
      headers: { 'content-type': 'multipart/form-data' },
    }
  );

export const getMenuImportAiPreview = (
  locationId: string | number,
  brandId: string | number,
  jobId: string
) =>
  api.get<ResponseType<MenuImportAiPreviewResponse>>(
    `/v2/menu/${locationId}/category/${brandId}/import-ai/preview/${jobId}`
  );

/** Publishes one menu, to the providers assigned to it only. */
export const publishMenu = (
  locationId: string | number,
  brandId: string | number,
  data?: PublishMenuDto,
  menuId?: MenuId
) =>
  api.post<ResponseType<GetPublishSyncStatusResponse>>(
    `/v2/menu/${locationId}/publish/${brandId}`,
    data,
    menuIdParams(menuId)
  );

export const getSyncStatus = (
  locationId: string | number,
  brandId: string | number,
  menuId?: MenuId
) =>
  api.get<ResponseType<GetPublishSyncStatusResponse>>(
    `/v2/menu/${locationId}/sync-status/${brandId}`,
    menuIdParams(menuId)
  );

/**
 * No `menuId` here on purpose: a provider always gets the menu assigned to
 * it, whichever menu is open on screen.
 */

export const publishMenuForSpecifiedProvider = (
  locationId: string | number,
  brandId: string | number,
  provider: Provider
) =>
  api.post<ResponseType<GetPublishSyncStatusResponse>>(
    `/v2/menu/${locationId}/publish/${brandId}/provider/${provider}`
  );

/** Every category in `list` has to belong to that menu. */
export const updateMenuCategoryOrder = (
  locationId: string | number,
  brandId: string | number,
  list: UpdateMenuListOrders[],
  menuId?: MenuId
) =>
  api.put<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/category/${brandId}/order`,
    { list },
    menuIdParams(menuId)
  );

export const updateMenuItemOrder = (
  locationId: string | number,
  brandId: string | number,
  categoryId: number,
  list: UpdateMenuListOrders[]
) =>
  api.put<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/category/${brandId}/order/${categoryId}/items`,
    { list }
  );

/**
 * `list` must hold every option of the group, numbered 1..n. No `menuId`: the
 * group already belongs to one menu. Like any edit it only flags the menu for
 * a publish.
 */
export const updateMenuModifierOptionsOrder = (
  locationId: string | number,
  brandId: string | number,
  modifierGroupId: number,
  list: UpdateMenuListOrders[]
) =>
  api.put<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/modifier-group/${brandId}/order/${modifierGroupId}/options`,
    { list }
  );

export const getPosItemsImport = (
  locationId: string | number,
  brandId: string | number
) =>
  api.get<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/location/${brandId}/brand/pos`
  );

/** Imports the chosen POS categories into the selected menu. */
export const importPosItems = (
  locationId: string | number,
  brandId: string | number,
  data: ImportItemsFromPosMenuDto,
  menuId?: MenuId
) =>
  api.post<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/location/${brandId}/brand/pos`,
    data,
    menuIdParams(menuId)
  );

/**
 * Per menu. Besides manual publishes it lists automatic ones
 * (`publishedBy: null`): retries, provider connections, POS syncs - only when
 * the content actually changed.
 */
export const getMenuPublishHistory = (
  locationId: string | number,
  brandId: string | number,
  params: PaginationParams<{ initialLimit?: number }>,
  menuId?: MenuId
) =>
  api.get<ResponseType<MenuPublishHistoryItemResponse[]>>(
    `/v2/menu/${locationId}/publish-history/${brandId}`,
    menuIdParams(menuId, params)
  );

export const getMenuPublishHistoryDetail = (
  locationId: string | number,
  brandId: string | number,
  historyId: string | number
) =>
  api.get<ResponseType<MenuPublishHistoryDetailResponse>>(
    `/v2/menu/${locationId}/publish-history/${brandId}/history/${historyId}`
  );

/** A version goes back into the menu it was published from. */
export const restoreMenuPublishHistory = (
  locationId: string | number,
  brandId: string | number,
  historyId: string | number
) =>
  api.post<ResponseType<MenuV2Response>>(
    `/v2/menu/${locationId}/publish-history/${brandId}/history/${historyId}/restore`
  );
